import React from 'react';
import styled from 'styled-components';

import { Label, Tooltip } from '@block-explorer/ui';

import {
  BodyText,
  DEFAULT_AMOUNT_PRECISION,
  FlexColumn,
  FlexRow,
  formatNumber,
  InfoIcon,
} from '@make-software/cspr-design';
import { useGetMetrics } from '../../hooks';
import { useTranslation } from 'react-i18next';
import { useCurrencyRateAmount } from '../../hooks/event-store-hooks';
import FiatCurrency from '../../components/multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { InfoSvgIcon } from '../../components';
import Skeleton from 'react-loading-skeleton';
import { getMetricsPercentage } from '../../scenes/HomeScene/utils';

const Container = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    flexDirection: ['row'],
    justifyContent: ['flex-start'],
    width: '100%',
    '> div': {
      minWidth: '80px',
    },
    '> * + *': {
      marginLeft: [0, 16, 24, 32],
    },
  })
);

const NavMetricsCell = ({
  loading,
  value,
  label,
  icon,
}: {
  loading: boolean;
  value: JSX.Element | string | null;
  label: string;
  icon?: JSX.Element;
}) => {
  return (
    <FlexColumn>
      <BodyText size={1} scale={'lg'} variation="white">
        {loading ? <Skeleton /> : value}
      </BodyText>
      <FlexRow align={'center'}>
        <Label variation="gray" transform={'uppercase'}>
          {label}
        </Label>
        {icon ? icon : null}
      </FlexRow>
    </FlexColumn>
  );
};

export const NavMetrics = React.memo(() => {
  const { t } = useTranslation();
  const { data: metricsData } = useGetMetrics();
  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const { apyPercent } = getMetricsPercentage(metricsData);

  const csprCurrencyRate = rateData?.data?.amount?.toString();

  const loading =
    metricsData == null || rateData?.data == null || loadingRateData;

  return (
    <Container>
      <NavMetricsCell
        loading={loading}
        value={metricsData ? formatNumber(metricsData.blockHeight) : null}
        label={t('Latest Block Height')}
      />
      <NavMetricsCell
        loading={loading}
        value={`${apyPercent && formatNumber(apyPercent, { precision: 2 })}%`}
        label={t('APY')}
        icon={
          <Tooltip
            tooltipContent={t(
              "Expected annual percentage yield on the staked tokens without taking the validator's fee into account"
            )}
          >
            <InfoSvgIcon src={InfoIcon} style={{ marginLeft: 3 }} size={12} />
          </Tooltip>
        }
      />
      <NavMetricsCell
        loading={loading}
        value={
          <FiatCurrency
            amount={csprCurrencyRate}
            precision={DEFAULT_AMOUNT_PRECISION}
          />
        }
        label={t('CSPR price')}
      />
      <NavMetricsCell
        loading={loading}
        value={metricsData && formatNumber(metricsData.circulatingSupply)}
        label={t('Circulating Supply')}
      />
    </Container>
  );
});
